/**
 * Orders Tab Component - Recent purchase orders list
 */

import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Package } from 'lucide-react';
import { PurchaseOrder, SupplierProfile } from '../../lib/api';
import { getStatusBadgeColor } from './utils';
import { useFormatters } from '../../contexts/PreferencesContext';

interface OrdersTabProps {
  supplier: SupplierProfile;
  orders: PurchaseOrder[];
  ordersLoading: boolean;
  formatCurrency: (value: string | number) => string;
}

export function OrdersTab({ supplier, orders, ordersLoading, formatCurrency }: OrdersTabProps) {
  const { formatDate } = useFormatters();
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Recent Purchase Orders
        </CardTitle>
      </CardHeader>
      <CardContent>
        {ordersLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No purchase orders found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.id}
                className="border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/purchasing?po=${order.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold">{order.po_number}</p>
                    <p className="text-sm text-muted-foreground">{order.customer_name}</p>
                  </div>
                  <Badge className={`${getStatusBadgeColor(order.status)} capitalize px-3 py-1 text-xs font-medium`}>
                    {order.status.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Issue Date: </span>
                    <span>{formatDate(order.issue_date)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Value: </span>
                    <span className="font-semibold">
                      {order.currency} {formatCurrency(order.total_value)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Lines: </span>
                    <span>{order.line_count || 0}</span>
                  </div>
                </div>
              </div>
            ))}
            {orders.length === 10 && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate(`/purchasing?supplier=${supplier.company}`)}
              >
                View All Orders
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
